import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Clock, Phone, Package, Palette, CheckCircle, XCircle, 
  MessageSquare, Loader2, AlertCircle, Filter, Users
} from 'lucide-react';
import { apiService } from '../../services/api';

export const FilaEsperaPage = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSubAdmin = user?.tipo === 'sub_admin';
  const [cadastros, setCadastros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [erro, setErro] = useState(null);

  const statusLabels = {
    aguardando: { label: 'Aguardando', cor: 'yellow', icon: Clock },
    contatado: { label: 'Contatado', cor: 'blue', icon: MessageSquare },
    convertido: { label: 'Convertido', cor: 'green', icon: CheckCircle },
    desistiu: { label: 'Desistiu', cor: 'red', icon: XCircle }
  };

  const planoLabels = {
    mensal: 'Plano Mensal',
    anual: 'Plano Anual',
    fidelidade: 'Plano Fidelidade'
  };

  useEffect(() => {
    carregarCadastros();
  }, [filtroStatus]);

  const carregarCadastros = async () => {
    try {
      setLoading(true);
      const response = await apiService.listarFilaEspera(filtroStatus);
      setCadastros(response.cadastros);
      setErro(null);
    } catch (error) {
      setErro('Erro ao carregar fila de espera');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleContatar = async (cadastroId) => {
    try {
      const response = await apiService.contatarFilaEspera(cadastroId);
      if (response.whatsapp_link) {
        window.open(response.whatsapp_link, '_blank');
        await carregarCadastros(); // Recarregar para atualizar status
      }
    } catch (error) {
      alert('Erro ao gerar link do WhatsApp');
      console.error(error);
    }
  };

  const handleMudarStatus = async (cadastroId, novoStatus) => {
    try {
      await apiService.atualizarFilaEspera(cadastroId, { status: novoStatus });
      await carregarCadastros();
    } catch (error) {
      alert('Erro ao atualizar status');
      console.error(error);
    }
  };

  const handleRemover = async (cadastroId, nome) => {
    if (!confirm(`Tem certeza que deseja remover ${nome} da fila?`)) return;

    try {
      await apiService.removerFilaEspera(cadastroId);
      await carregarCadastros();
    } catch (error) {
      alert('Erro ao remover cadastro');
      console.error(error);
    }
  };

  const formatarData = (dataISO) => {
    if (!dataISO) return 'N/A';
    const data = new Date(dataISO);
    return data.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const diasDesde = (dataISO) => {
    const data = new Date(dataISO);
    const agora = new Date();
    const diff = Math.floor((agora - data) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Hoje';
    if (diff === 1) return '1 dia atrás';
    return `${diff} dias atrás`;
  };

  const stats = {
    total: cadastros.length,
    aguardando: cadastros.filter(c => c.status === 'aguardando').length,
    contatado: cadastros.filter(c => c.status === 'contatado').length,
    convertido: cadastros.filter(c => c.status === 'convertido').length
  };

  if (loading && cadastros.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Carregando...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Clock className="w-8 h-8 text-blue-600" />
          Fila de Espera
        </h1>
        <p className="text-gray-600 mt-1">
          Gerenciar clientes interessados aguardando disponibilidade de motos
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total na Fila</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <Users className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Aguardando</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.aguardando}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Contatados</p>
                <p className="text-3xl font-bold text-blue-600">{stats.contatado}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Convertidos</p>
                <p className="text-3xl font-bold text-green-600">{stats.convertido}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Filtrar por status:</span>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={filtroStatus === '' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltroStatus('')}
              >
                Todos
              </Button>
              {Object.entries(statusLabels).map(([key, { label }]) => (
                <Button
                  key={key}
                  variant={filtroStatus === key ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFiltroStatus(key)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Cadastros */}
      {erro && (
        <Alert className="bg-red-50 border-red-200">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">{erro}</AlertDescription>
        </Alert>
      )}

      {cadastros.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">Nenhum cadastro na fila de espera</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {cadastros.map((cadastro) => {
            const StatusIcon = statusLabels[cadastro.status]?.icon || Clock;
            const statusInfo = statusLabels[cadastro.status] || statusLabels.aguardando;

            return (
              <Card key={cadastro.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Informações Principais */}
                    <div className="flex-1 space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            {cadastro.nome}
                            <span className={`
                              inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium
                              ${statusInfo.cor === 'yellow' ? 'bg-yellow-100 text-yellow-800' : ''}
                              ${statusInfo.cor === 'blue' ? 'bg-blue-100 text-blue-800' : ''}
                              ${statusInfo.cor === 'green' ? 'bg-green-100 text-green-800' : ''}
                              ${statusInfo.cor === 'red' ? 'bg-red-100 text-red-800' : ''}
                            `}>
                              <StatusIcon className="w-3 h-3" />
                              {statusInfo.label}
                            </span>
                          </h3>
                          <p className="text-sm text-gray-500">
                            Cadastro: {formatarData(cadastro.data_cadastro)} ({diasDesde(cadastro.data_cadastro)})
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{cadastro.telefone}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-gray-400" />
                          <span>{planoLabels[cadastro.plano_interesse]}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Palette className="w-4 h-4 text-gray-400" />
                          <span>{cadastro.cor_preferencia}</span>
                        </div>
                      </div>

                      {cadastro.observacoes && (
                        <div className="bg-gray-50 rounded p-3 text-sm text-gray-700">
                          <span className="font-medium">Observações:</span> {cadastro.observacoes}
                        </div>
                      )}

                      {cadastro.data_contato && (
                        <p className="text-xs text-gray-500">
                          Último contato: {formatarData(cadastro.data_contato)}
                        </p>
                      )}
                    </div>

                    {/* Ações */}
                    <div className="flex flex-col gap-2 min-w-[200px]">
                      {!isSubAdmin && cadastro.status === 'aguardando' && (
                        <Button
                          data-testid={`btn-contatar-${cadastro.id}`}
                          onClick={() => handleContatar(cadastro.id)}
                          className="bg-green-600 hover:bg-green-700 w-full"
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Contatar via WhatsApp
                        </Button>
                      )}

                      {!isSubAdmin && cadastro.status === 'contatado' && (
                        <div className="space-y-2">
                          <Button
                            onClick={() => handleContatar(cadastro.id)}
                            variant="outline"
                            className="w-full"
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Contatar Novamente
                          </Button>
                          <Button
                            onClick={() => handleMudarStatus(cadastro.id, 'convertido')}
                            className="bg-green-600 hover:bg-green-700 w-full"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Marcar como Convertido
                          </Button>
                        </div>
                      )}

                      {!isSubAdmin && (cadastro.status === 'aguardando' || cadastro.status === 'contatado') && (
                        <Button
                          onClick={() => handleMudarStatus(cadastro.id, 'desistiu')}
                          variant="outline"
                          className="text-red-600 hover:bg-red-50 w-full"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Marcar como Desistiu
                        </Button>
                      )}

                      {!isSubAdmin && (
                        <Button
                          onClick={() => handleRemover(cadastro.id, cadastro.nome)}
                          variant="outline"
                          className="text-gray-600 hover:bg-white/5 w-full"
                        >
                          Remover da Fila
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
